import type { Tag } from "@/components/tags/types";

import type { RuleNode, StopNodeData } from "../types/nodes";

export const normalizeStopTagNames = (tags: unknown): string[] => {
	if (!Array.isArray(tags)) return [];
	return tags
		.map((tag) => {
			if (typeof tag === "string") return tag.trim();
			if (tag && typeof tag === "object" && "name" in tag && typeof (tag as { name: unknown }).name === "string") {
				return (tag as { name: string }).name.trim();
			}
			return "";
		})
		.filter(Boolean);
};

export const serializeStopNodeDataForSave = (data: {
	name?: string;
	comment?: string;
	tags?: unknown;
}): StopNodeData => ({
	type: "stop",
	name: data.name,
	comment: data.comment ?? "",
	tags: normalizeStopTagNames(data.tags),
});

/** Ensure main-canvas stop nodes store tags as string names (load + pre-save). */
export const normalizeStopTagsInNodes = (nodes: RuleNode[]): RuleNode[] =>
	nodes.map((node): RuleNode => {
		if (node.type !== "stop") return node;
		return {
			...node,
			data: {
				...node.data,
				tags: normalizeStopTagNames(node.data.tags),
			},
		};
	});

/** Collect tag names from stop nodes on the main canvas. */
export const collectStopTagNamesFromNodes = (nodes: RuleNode[]): string[] => {
	const names: string[] = [];
	for (const node of nodes) {
		if (node.type === "stop") {
			names.push(...normalizeStopTagNames(node.data.tags));
		}
	}
	return names;
};

/** Org API tags + in-rule stop tags, case-insensitive dedup. Org names win on collision. */
export const mergeStopTagNames = (orgTags: Tag[], ruleTagNames: string[]): string[] => {
	const seen = new Set<string>();
	const merged: string[] = [];

	for (const tag of orgTags) {
		const name = tag.name?.trim();
		if (!name) continue;
		const key = name.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		merged.push(name);
	}

	for (const name of ruleTagNames) {
		const trimmed = name.trim();
		if (!trimmed) continue;
		const key = trimmed.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		merged.push(trimmed);
	}

	return merged.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
};
